import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Entity } from '@inter-ikea-core/core';
import { RootState } from 'ducks';
import { isHamburgerMenuOpen, isShowGroupNav } from './layout';

export interface SheetsState {
  sheetsFiltersSheetVisible: boolean;
  loadVpcSheetVisible: boolean;
  loadVpcSheetVisibleOnExit: boolean;
  saveVpcSheetVisible: boolean;
  saveVpcSheetBack: boolean;
  saveVpcSheetOpenedFromPrint: boolean;
  bookAppointmentSheetVisible: boolean;
  storeSelectorSheetVisible: boolean;
  unsavedChangesSheetVisible: boolean;
  unsavedChangesSheetLeaveAction?(): void;
  unsavedChangesSheetDismissed: boolean;
  availabilitySheetVisible: boolean;
  unavailableFramesMenuVisible: boolean;
  showPrintQuestionSheet: boolean;
  showPrintOptionsSheet: boolean;
  showSlidingDoorsInfoSheet: boolean;
  leftFromBackButton: boolean;
  frontStyleSheet: { visible?: boolean; apply?: boolean; entity?: Entity } | undefined;
  cantOpenDesignModal: boolean;
  softClosingHandleId: string;
  resumeLastSessionModalOpen: boolean;
  saveDesignToProfile: boolean;
  openDesign: boolean;
  showSavedDesignsTab: boolean;
  openDesignSheetVisibleOnExit: boolean;
  supportLegChoice: { forgetChoice: boolean; productRef?: string };
  shareDesignCard: boolean;
  acceptedDeleteIncludedArticles: boolean;
}

const initialState: SheetsState = {
  sheetsFiltersSheetVisible: false,
  loadVpcSheetVisible: false,
  loadVpcSheetVisibleOnExit: false,
  saveVpcSheetVisible: false,
  saveVpcSheetBack: false,
  saveVpcSheetOpenedFromPrint: false,
  bookAppointmentSheetVisible: false,
  storeSelectorSheetVisible: false,
  unsavedChangesSheetVisible: false,
  unsavedChangesSheetDismissed: false,
  availabilitySheetVisible: false,
  unavailableFramesMenuVisible: false,
  showPrintQuestionSheet: false,
  showPrintOptionsSheet: false,
  showSlidingDoorsInfoSheet: false,
  leftFromBackButton: false,
  frontStyleSheet: {},
  cantOpenDesignModal: false,
  softClosingHandleId: '',
  resumeLastSessionModalOpen: false,
  saveDesignToProfile: false,
  openDesign: false,
  showSavedDesignsTab: false,
  openDesignSheetVisibleOnExit: false,
  supportLegChoice: { forgetChoice: true },
  shareDesignCard: false,
  acceptedDeleteIncludedArticles: false
};

// Reducer and actions
const sheetsSlice = createSlice({
  name: 'sheets',
  initialState,
  reducers: {
    resetSheets(state) {
      Object.assign(state, initialState);
    },
    showSaveVpcSheet(state, action: PayloadAction<boolean>) {
      state.saveVpcSheetVisible = true;
      state.saveVpcSheetBack = action.payload;
    },
    leaveFromBackButton(state, action: PayloadAction<boolean>) {
      state.leftFromBackButton = action.payload;
    },
    resetLeaveFromBackButton(state) {
      state.leftFromBackButton = false;
    },
    hideSaveVpcSheet(state) {
      state.saveVpcSheetVisible = false;
    },
    setSaveVpcSheetOpenedFromPrint(state, action: PayloadAction<boolean>) {
      state.saveVpcSheetOpenedFromPrint = action.payload;
    },
    showBookAppointmentSheet(state) {
      state.bookAppointmentSheetVisible = true;
    },
    hideBookAppointmentSheet(state) {
      state.bookAppointmentSheetVisible = false;
    },
    showStoreSelectorSheet(state) {
      state.storeSelectorSheetVisible = true;
    },
    hideStoreSelectorSheet(state) {
      state.storeSelectorSheetVisible = false;
    },
    setLoadVpcSheetVisible(state, action: PayloadAction<boolean>) {
      state.loadVpcSheetVisible = action.payload;
    },
    setLoadVpcSheetVisibleOnExit(state, action: PayloadAction<boolean>) {
      state.loadVpcSheetVisibleOnExit = action.payload;
    },
    showUnsavedChangesSheet(state, action: PayloadAction<(() => void) | undefined>) {
      state.unsavedChangesSheetVisible = true;
      state.unsavedChangesSheetLeaveAction = action?.payload;
    },
    hideUnsavedChangesSheet(state) {
      state.unsavedChangesSheetVisible = false;
    },
    setUnsavedChangesSheetDimissed(state, action: PayloadAction<boolean>) {
      state.unsavedChangesSheetDismissed = action.payload;
    },
    setAvailabilitySheetVisible(state, action: PayloadAction<boolean>) {
      state.availabilitySheetVisible = action.payload;
    },
    showUnavailableFramesMenu(state, action: PayloadAction<boolean>) {
      state.unavailableFramesMenuVisible = action.payload;
    },
    showPrintQuestionSheet(state, action: PayloadAction<boolean>) {
      state.showPrintQuestionSheet = action.payload;
    },
    showPrintOptionsSheet(state, action: PayloadAction<boolean>) {
      state.showPrintOptionsSheet = action.payload;
    },
    showSlidingDoorsInfoSheet(state) {
      state.showSlidingDoorsInfoSheet = true;
    },
    hideSlidingDoorsInfoSheet(state) {
      state.showSlidingDoorsInfoSheet = false;
    },
    setFrontStyleSheetVisible(state, action: PayloadAction<Entity | undefined>) {
      state.frontStyleSheet = { visible: true, entity: action.payload };
    },
    setFrontStyleSheetNotVisible(state, action: PayloadAction<boolean>) {
      state.frontStyleSheet = { visible: false, apply: action.payload };
    },
    resetFrontStyleSheet(state) {
      state.frontStyleSheet = {};
    },
    showCantOpenDesignModal(state) {
      state.cantOpenDesignModal = true;
    },
    hideCantOpenDesignModal(state) {
      state.cantOpenDesignModal = false;
    },
    showResumeLastSessionModal(state) {
      state.resumeLastSessionModalOpen = true;
    },
    hideResumeLastSessionModal(state) {
      state.resumeLastSessionModalOpen = false;
    },
    showSaveDesignToProfileSheet(state) {
      state.saveDesignToProfile = true;
    },
    hideSaveDesignToProfileSheet(state) {
      state.saveDesignToProfile = false;
    },
    showShareDesignCard(state) {
      state.shareDesignCard = true;
    },
    hideShareDesignCard(state) {
      state.shareDesignCard = false;
    },
    showOpenDesignSheet(state, action: PayloadAction<boolean>) {
      state.openDesign = true;
      state.showSavedDesignsTab = action.payload;
    },
    hideOpenDesignSheet(state) {
      state.openDesign = false;
    },
    setOpenDesignSheetVisibleOnExit(state, action: PayloadAction<boolean>) {
      state.openDesignSheetVisibleOnExit = action.payload;
    },
    setSupportLegChoice(
      state,
      action: PayloadAction<{ forgetChoice: boolean; productRef: string | undefined }>
    ) {
      state.supportLegChoice = action.payload;
    },
    setAcceptDeleteIncludedArticles(state, action: PayloadAction<boolean>) {
      state.acceptedDeleteIncludedArticles = action.payload;
    }
  }
});

// Selectors
export const isVpcSheetOpen = (state: RootState) => state.sheets.saveVpcSheetVisible;
export const isBookAppointmentSheetOpen = (state: RootState) =>
  state.sheets.bookAppointmentSheetVisible;
export const isStoreSelectorSheetOpen = (state: RootState) =>
  state.sheets.storeSelectorSheetVisible;
export const isLoadVpcSheetVisible = (state: RootState) => state.sheets.loadVpcSheetVisible;
export const isLoadVpcSheetVisibleOnExit = (state: RootState) =>
  state.sheets.loadVpcSheetVisibleOnExit;
export const getSaveVpcSheetOpenedFromPrint = (state: RootState) =>
  state.sheets.saveVpcSheetOpenedFromPrint;
export const isUnsavedChangesSheetVisible = (state: RootState) =>
  state.sheets.unsavedChangesSheetVisible;
export const isUnsavedChangesSheetDismissed = (state: RootState) =>
  state.sheets.unsavedChangesSheetDismissed;
export const unsavedChangesLeaveAction = (state: RootState) =>
  state.sheets.unsavedChangesSheetLeaveAction;
export const hasSaveVpcSheetBack = (state: RootState) => state.sheets.saveVpcSheetBack;
export const isPrintQuestionSheet = (state: RootState) => state.sheets.showPrintQuestionSheet;
export const isPrintOptionsSheet = (state: RootState) => state.sheets.showPrintOptionsSheet;
export const leftFromBackButton = (state: RootState) => state.sheets.leftFromBackButton;
export const isUnavailableFramesMenuVisible = (state: RootState) =>
  state.sheets.unavailableFramesMenuVisible;
export const isSlidingDoorsInfoSheetVisible = (state: RootState) =>
  state.sheets.showSlidingDoorsInfoSheet;
export const isAvailabilitySheetVisible = (state: RootState) =>
  state.sheets.availabilitySheetVisible;
export const isFrontStyleSheetOpen = (state: RootState) => !!state.sheets.frontStyleSheet?.visible;
export const getFrontStyleSheetEntity = (state: RootState) => state.sheets.frontStyleSheet?.entity;
export const isSameFrontStyleApplied = (state: RootState) => state.sheets.frontStyleSheet?.apply;

export const frontStyleState = (state: RootState) => state.sheets.frontStyleSheet;
export const isCantOpenDesignModalVisible = (state: RootState) => state.sheets.cantOpenDesignModal;
export const getSoftClosingHandleId = (state: RootState) => state.sheets.softClosingHandleId;

export const isResumeLastSessionModalOpen = (state: RootState) =>
  state.sheets.resumeLastSessionModalOpen;

export const isSaveDesignToProfileSheetOpen = (state: RootState) =>
  state.sheets.saveDesignToProfile;
export const isShareDesignCardOpen = (state: RootState) => state.sheets.shareDesignCard;
export const isOpenDesignSheetOpen = (state: RootState) => state.sheets.openDesign;
export const isShowSavedDesignsTabEnabled = (state: RootState) => state.sheets.showSavedDesignsTab;
export const isOpenDesignSheetVisibleOnExit = (state: RootState) =>
  state.sheets.openDesignSheetVisibleOnExit;
export const getSupportLegChoice = (state: RootState) => state.sheets.supportLegChoice;
export const getAcceptedDeleteIncludedArticles = (state: RootState) =>
  state.sheets.acceptedDeleteIncludedArticles;

export const isLightboxOpen = createSelector(
  [
    isVpcSheetOpen,
    isBookAppointmentSheetOpen,
    isLoadVpcSheetVisible,
    isUnsavedChangesSheetVisible,
    isShowGroupNav,
    isHamburgerMenuOpen
  ],
  (
    vpcSheet,
    bookAppointmentSheet,
    loadVpcSheet,
    unsavedChangesSheet,
    isShowGroupNav,
    isHamburgerMenuOpen
  ) =>
    vpcSheet ||
    bookAppointmentSheet ||
    loadVpcSheet ||
    unsavedChangesSheet ||
    isShowGroupNav ||
    isHamburgerMenuOpen
);

export const {
  hideBookAppointmentSheet,
  hideSaveVpcSheet,
  hideSlidingDoorsInfoSheet,
  hideStoreSelectorSheet,
  hideUnsavedChangesSheet,
  hideCantOpenDesignModal,
  hideSaveDesignToProfileSheet,
  hideShareDesignCard,
  hideOpenDesignSheet,
  leaveFromBackButton,
  resetLeaveFromBackButton,
  resetSheets,
  setAvailabilitySheetVisible,
  setLoadVpcSheetVisible,
  setLoadVpcSheetVisibleOnExit,
  setSaveVpcSheetOpenedFromPrint,
  setUnsavedChangesSheetDimissed,
  setOpenDesignSheetVisibleOnExit,
  showBookAppointmentSheet,
  showPrintOptionsSheet,
  showPrintQuestionSheet,
  showSaveVpcSheet,
  showSlidingDoorsInfoSheet,
  showStoreSelectorSheet,
  showUnavailableFramesMenu,
  showUnsavedChangesSheet,
  showSaveDesignToProfileSheet,
  showOpenDesignSheet,
  showShareDesignCard,
  setFrontStyleSheetVisible,
  setFrontStyleSheetNotVisible,
  resetFrontStyleSheet,
  showCantOpenDesignModal,
  showResumeLastSessionModal,
  hideResumeLastSessionModal,
  setSupportLegChoice,
  setAcceptDeleteIncludedArticles
} = sheetsSlice.actions;

export default sheetsSlice.reducer;
